import { CategoryService } from '../../services/category.service';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ErrorService } from '../../../../core/services/error.service';
import { CategoryDto } from '../../dtos/category.dto';
import { AppointmentDistancePipe } from '../../../location/pipes/appointment-distance.pipe';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { CategoryFormFactory } from '../../mappers/category-form.factory';
import { CategoryMapper } from '../../mappers/category.mapper';
import { CategoryModel } from '../../models/category.model';
import { UpdateCategoryDto } from '../../dtos/update-category.dto';
import { LanguagePipe } from '../../../location/pipes/language.pipe';
import { MatTabsModule } from '@angular/material/tabs';
import { LocationSummaryDto } from '../../../location/dtos/location-summary.dto';
import { BookingConstraintDto } from '../../../../shared/dtos/booking-constraint.dto';
import {
  RestrictBookingTimesDialog
} from '../../../../shared/dialogs/restrict-booking-times/restrict-booking-times.dialog';
import { FormFactory } from '../../../../shared/mappers/form.factory';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-manage-category',
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, AppointmentDistancePipe, MatSelectModule, MatIconModule, MatCardModule, LanguagePipe, MatTabsModule, TranslocoPipe],
  templateUrl: './manage-category.dialog.html',
  styleUrl: './manage-category.dialog.scss'
})
export class ManageCategoryDialog implements OnInit {
  categoryForm!: FormGroup;
  locationIds!: number[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      categoryDto?: CategoryDto,
      categoryGroups: { language: string, values: string[] }[],
      locationSummaryDtos: LocationSummaryDto[],
    },
    private dialogRef: MatDialogRef<ManageCategoryDialog>,
    private fb: FormBuilder,
    private categoryService: CategoryService,
    private errorService: ErrorService,
    private translocoService: TranslocoService,
    private dialog: MatDialog
  ) {
  }

  get name(): FormArray {
    return this.categoryForm.get('name') as FormArray;
  }

  get bookingHint(): FormArray {
    return this.categoryForm.get('bookingHint') as FormArray;
  }

  get categoryGroup(): FormArray {
    return this.categoryForm.get('categoryGroup') as FormArray;
  }

  get bookingConstraints(): FormArray {
    return this.categoryForm.get('bookingConstraints') as FormArray;
  }

  categoryGroups(language: string): string[] {
    return this.data.categoryGroups.find(group => group.language === language)?.values ?? [];
  }

  ngOnInit(): void {
    this.locationIds = [0, ...this.data.locationSummaryDtos?.map(location => location.locationId)];

    if (!this.data.categoryDto) {
      const emptyCategoryModel: CategoryModel = {
        name: [
          { language: 'en', value: undefined },
          { language: 'de', value: undefined },
          { language: 'fr', value: undefined },
          { language: 'it', value: undefined }
        ],
        categoryGroup: [
          { language: 'en', value: undefined },
          { language: 'de', value: undefined },
          { language: 'fr', value: undefined },
          { language: 'it', value: undefined }
        ],
        bookingHint: [
          { language: 'en', value: undefined },
          { language: 'de', value: undefined },
          { language: 'fr', value: undefined },
          { language: 'it', value: undefined }
        ],
        bookingConstraints: [],
        locationId: 0
      };
      this.categoryForm = CategoryFormFactory.createCategoryForm(this.fb, emptyCategoryModel);
      return;
    }

    this.data.categoryDto.locationId = this.data.categoryDto.locationId ?? 0;
    this.categoryForm = CategoryFormFactory.createCategoryForm(this.fb, CategoryMapper.toModel(this.data.categoryDto));
  }

  locationName(locationId: number): string {
    if (locationId == 0) {
      return this.translocoService.translate("label.location.null");
    }

    let locationSummaryDto = this.data.locationSummaryDtos.find((locationPreviewDto) => locationPreviewDto.locationId == locationId);
    return `${locationSummaryDto?.name} - ${locationSummaryDto?.city}, ${locationSummaryDto?.street}`;
  }

  close(): void {
    this.dialogRef.close(false);
  }

  manage(): void {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }

    const updateCategoryDto: UpdateCategoryDto = CategoryMapper.toDto(this.categoryForm.value);

    if (!this.data.categoryDto) {
      this.categoryService.createCategory(updateCategoryDto).subscribe({
        next: () => this.dialogRef.close(true),
        error: (error) => {
          this.errorService.handleError(error);
        }
      });
      return;
    }

    this.categoryService.updateCategory(updateCategoryDto).subscribe({
      next: () => this.dialogRef.close(true),
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  restrictBookingTimes(): void {
    let bookingConstraintDtos: BookingConstraintDto[] = this.categoryForm.get('bookingConstraints')?.value;

    this.dialog
      .open(RestrictBookingTimesDialog, { autoFocus: false, data: bookingConstraintDtos })
      .afterClosed()
      .subscribe((bookingConstraints?: BookingConstraintDto[]) => {
        if (bookingConstraints) {
          this.categoryForm.setControl('bookingConstraints', FormFactory.createBookingConstraintsForm(this.fb, bookingConstraints ?? []));
        }
      });
  }
}
